import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import './Services.css';

const Services = () => {
    const [services, setServices] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedStringBrand, setSelectedStringBrand] = useState(null);
    const [selectedGripType, setSelectedGripType] = useState('overgrip');
    const [expressAvailable, setExpressAvailable] = useState(true);

    useEffect(() => {
        // Handle scroll to section if hash is present in URL
        const scrollToSection = () => {
            const hash = window.location.hash;
            if (hash) {
                console.log('Scrolling to section:', hash);
                const element = document.querySelector(hash);
                if (element) {
                    console.log('Found element:', element);
                    setTimeout(() => {
                        // Get the element's position and add some offset
                        const elementPosition = element.offsetTop;
                        const offsetPosition = elementPosition - 80; // 80px offset from top

                        window.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                        });
                    }, 300); // Increased timeout to ensure content is loaded
                } else {
                    console.log('Element not found for hash:', hash);
                }
            }
        };

        const fetchServices = async () => {
            try {
                console.log('Fetching services from API...');
                const apiUrl = `${API_BASE_URL}/services`;
                console.log('API URL:', apiUrl);
                const response = await fetch(apiUrl);
                console.log('Response status:', response.status);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                console.log('Services data received:', data);

                // Transform old stringTypes structure to new stringBrands structure if needed
                if (data.stringTypes && !data.stringBrands) {
                    data.stringBrands = data.stringTypes;
                    delete data.stringTypes;
                }

                // Ensure grommets has the halfSet/fullSet structure
                if (!data.grommets || !data.grommets.halfSet) {
                    data.grommets = {
                        halfSet: {
                            fee: "10",
                            description: "Replace grommets on one side of the racquet frame",
                            note: "Ideal for localised wear or partial damage"
                        },
                        fullSet: {
                            fee: "14",
                            description: "Complete grommet set replacement for the entire racquet frame",
                            note: "Recommended for heavily worn or damaged grommets"
                        }
                    };
                }

                setServices(data);
                if (data.expressService) {
                    setExpressAvailable(data.expressService.available);
                }
            } catch (error) {
                console.error('Error fetching services:', error);
                // Set fallback mock data if API fails
                setServices(getMockServices());
                setExpressAvailable(true);
            } finally {
                setLoading(false);
            }
        };

        fetchServices().then(() => {
            // Scroll after services are loaded
            setTimeout(scrollToSection, 100);
        }).catch(() => {
            // Still try to scroll even if API fails
            setTimeout(scrollToSection, 100);
        });

        // Listen for hash changes
        const handleHashChange = () => {
            setTimeout(scrollToSection, 100);
        };
        window.addEventListener('hashchange', handleHashChange);

        // Cleanup event listener
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []);

    // Fallback mock data function
    const getMockServices = () => ({
        stringBrands: [
        ],
        gripServices: {
            overgrip: [
                {
                    brand: "Kumpoo",
                    model: "Pro Overgrip",
                    description: "Classic tacky feel with good moisture absorption",
                    price: "-",
                }
            ],
            replacementGrip: [
                {
                    brand: "Gosen",
                    model: "Cushion Aire Classic",
                    description: "Soft towel replacement grip",
                    price: "-",
                }
            ]
        },
        grommets: {
            halfSet: {
                fee: "10",
                description: "Replace grommets on one side of the racquet frame",
                note: "Ideal for localised wear or partial damage"
            },
            fullSet: {
                fee: "14",
                description: "Complete grommet set replacement for the entire racquet frame",
                note: "Recommended for heavily worn or damaged grommets"
            }
        },
        expressService: {
            available: true,
            deliveryTime: "1 hour",
            fee: "5",
            requirements: "Subject to stringer availability",
            unavailableMessage: "Express service is currently unavailable. No stringer present at this time."
        },
        standardStringing: {
            baseFee: "-",
            description: "Professional stringing service",
            turnaround: "Based on shop queue and stringer availability"
        }
    });

    const toggleStringBrand = (brandId) => {
        setSelectedStringBrand(selectedStringBrand === brandId ? null : brandId);
    };

    if (loading) {
        return <div className="loading">Loading...</div>;
    }

    if (!services) {
        return <div className="error">Error loading services</div>;
    }

    return (
        <div className="services-page">
            <div className="container">
                <header className="page-header">
                    <h1>Stringing Services</h1>
                    <p>Professional racquet stringing with premium strings and expert craftsmanship</p>
                </header>

                {/* Interactive String Brand Selection */}
                <section id="string-brands" className="string-brands-interactive">
                    <h2>Select String Brand</h2>
                    <p className="section-subtitle">Click on a brand to view available string models</p>

                    <div className="string-brand-accordion">
                        {services.stringBrands && services.stringBrands.length > 0 ? (
                            services.stringBrands.map(stringBrand => (
                                <div key={stringBrand.id} className="accordion-item">
                                    <div
                                        className={`accordion-header ${selectedStringBrand === stringBrand.id ? 'active' : ''}`}
                                        onClick={() => toggleStringBrand(stringBrand.id)}
                                    >
                                        <div className="header-content">
                                            <h3>{stringBrand.name}</h3>
                                            <p>{stringBrand.description}</p>
                                        </div>
                                        <div className="accordion-icon">
                                            {selectedStringBrand === stringBrand.id ? '−' : '+'}
                                        </div>
                                    </div>

                                    <div className={`accordion-content ${selectedStringBrand === stringBrand.id ? 'active' : ''}`}>
                                        <div className="strings-container">
                                            <div className="strings-grid">
                                                {stringBrand.strings && stringBrand.strings.map((string, index) => (
                                                    <div key={index} className="string-item">
                                                        <div className="string-header">
                                                            <h4>{stringBrand.name} {string.model}</h4>
                                                        </div>
                                                        <div className="string-type-badge">
                                                            <span className="type-badge">{string.type}</span>
                                                        </div>
                                                        <p className="string-description">{string.description}</p>

                                                        {string.material && (
                                                            <div className="string-material">
                                                                <h5>Material Composition:</h5>
                                                                <div className="material-details">
                                                                    <div className="material-item">
                                                                        <span className="material-label">Core:</span>
                                                                        <span className="material-value">{string.material.core}</span>
                                                                    </div>
                                                                    <div className="material-item">
                                                                        <span className="material-label">Outer:</span>
                                                                        <span className="material-value">{string.material.outer}</span>
                                                                    </div>
                                                                    <div className="material-item">
                                                                        <span className="material-label">Coating:</span>
                                                                        <span className="material-value">{string.material.coating}</span>
                                                                    </div>
                                                                    <div className="material-item">
                                                                        <span className="material-label">Gauge:</span>
                                                                        <span className="material-value">{string.material.gauge}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-brands-message">
                                <p>No string brands available at the moment.</p>
                            </div>
                        )}
                    </div>
                </section>


                {/* Grip Replacement Services */}
                <section className="grommet-services">
                    <h2>Grip Replacement</h2>

                    <div className="grommet-options">
                        <div className="grommet-option assisted-service">
                            <div className="option-header">
                                <h3>Overgrip</h3>
                            </div>
                            <p className="option-description">Thin wrap applied over the existing grip for added tackiness and sweat absorption.</p>
                            <p className="option-note">Quick replacement to freshen up grip feel and comfort.</p>
                            <div className="option-features">
                                <span className="feature">✓ Professional installation</span>
                                <span className="feature">✓ Improved grip feel</span>
                                <span className="feature">✓ Sweat absorption</span>
                            </div>
                        </div>

                        <div className="grommet-option assisted-service">
                            <div className="option-header">
                                <h3>Towel Grip</h3>
                            </div>
                            <p className="option-description">Soft towel replacement grip offering superior moisture absorption and cushioning for extended play.</p>
                            <p className="option-note">Ideal for players who prefer a thicker, more comfortable feel.</p>
                            <div className="option-features">
                                <span className="feature">✓ Professional installation</span>
                                <span className="feature">✓ Superior cushioning</span>
                                <span className="feature">✓ High moisture absorption</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Grommet Replacement Services */}
                <section className="grommet-services">
                    <h2>Grommet Replacement</h2>

                    <div className="grommet-options">
                        <div className="grommet-option assisted-service">
                            <div className="option-header">
                                <h3>Half Set Grommet Replacement</h3>
                            </div>
                            <p className="option-description">{services.grommets.halfSet.description}</p>
                            <p className="option-note">{services.grommets.halfSet.note}</p>
                            <div className="option-features">
                                <span className="feature">✓ Professional installation</span>
                                <span className="feature">✓ Expert guidance</span>
                                <span className="feature">✓ Quality assurance</span>
                            </div>
                        </div>

                        <div className="grommet-option assisted-service">
                            <div className="option-header">
                                <h3>Full Set Grommet Replacement</h3>
                            </div>
                            <p className="option-description">{services.grommets.fullSet.description}</p>
                            <p className="option-note">{services.grommets.fullSet.note}</p>
                            <div className="option-features">
                                <span className="feature">✓ Professional installation</span>
                                <span className="feature">✓ Expert guidance</span>
                                <span className="feature">✓ Quality assurance</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Express Stringing Service */}
                <section id="express-service" className="express-service">
                    <h2>Express Stringing Service</h2>

                    <div className="express-content express-content-single">
                        <div className={`express-option ${!expressAvailable ? 'unavailable' : ''}`}>
                            <div className="express-header">
                                <h3>Express Service</h3>
                                <span className={`availability-badge ${expressAvailable ? 'available' : 'unavailable'}`}>
                                    {expressAvailable ? '● Available' : '● Unavailable'}
                                </span>
                            </div>
                            <p className="express-description">Get your racquet strung in as little as 1 hour — perfect for same-day play.</p>
                            <p className="express-requirements">Subject to stringer availability. Please check with us before booking.</p>
                            {!expressAvailable && (
                                <div className="express-unavailable">
                                    <p className="unavailable-message">
                                        {services.expressService?.unavailableMessage || 'Express service is currently unavailable. No stringer present at this time.'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Booking CTA */}
                <section className="booking-cta">
                    <div className="cta-content">
                        <h2>Ready to Book Your Service?</h2>
                        <p>Contact us to schedule your stringing service or get personalized recommendations</p>
                        <div className="cta-buttons">
                            <a href="/contact" className="cta-button primary">
                                Book Now
                            </a>
                            <a href="tel:+6588048780" className="cta-button secondary">
                                Call Us
                            </a>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default Services;